import React, { useState, useEffect } from "react";
import editorange from "assets/images/icon/edit-orange.png";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCompany, updateCompany } from "services/company";
import Input from "components/Input";
import Select from "components/Select";

const TRANSFER_OPTIONS = [
    { value: "Yes", label: "Yes – transfer required" },
    { value: "No",  label: "No – not required" },
];

const buildForm = (company) => ({
    workersCompTransferRequired: company?.workersCompTransferRequired || "",
    workersCompCarrier:          company?.workersCompCarrier          || "",
    workersCompPolicyNumber:     company?.workersCompPolicyNumber     || "",
    workersCompEffectiveDate:    company?.workersCompEffectiveDate    || "",
    workersCompExpirationDate:   company?.workersCompExpirationDate   || "",
    workersCompCoveredStates:    company?.workersCompCoveredStates    || "",
    workersCompClassCodes:       company?.workersCompClassCodes       || "",
    workersCompExperienceMod:    company?.workersCompExperienceMod    || "",
    workersCompAgentName:        company?.workersCompAgentName        || "",
    workersCompAgentPhone:       company?.workersCompAgentPhone       || "",
    workersCompNotes:            company?.workersCompNotes            || "",
});

const Field = ({ label, value }) => (
    <div>
        <p className="m-0 title">{label}</p>
        <p className="m-0 sub-titles">{value || "—"}</p>
    </div>
);

const WorkersCompensationSection = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm]           = useState(buildForm(null));
    const queryClient               = useQueryClient();

    const { data: company, isLoading, isError } = useQuery({
        queryKey: ["company"],
        queryFn:  getCompany,
    });

    useEffect(() => {
        if (company) setForm(buildForm(company));
    }, [company]);

    const mutation = useMutation({
        mutationFn: updateCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["company"] });
            setIsEditing(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate({ ...form });
    };

    const handleCancel = () => {
        setForm(buildForm(company));
        setIsEditing(false);
        mutation.reset();
    };

    if (isLoading) return <p className="text-center mt-4">Loading workers compensation information...</p>;
    if (isError)   return <p className="text-danger mt-4">Failed to load workers compensation information.</p>;

    if (isEditing) {
        return (
            <div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h2 className="sub-title">Edit Workers Compensation</h2>
                </div>
                <form onSubmit={handleSave}>
                    <div className="border-bottom pb-4 mb-4">
                        <p className="info-title mb-3">Policy Information</p>
                        <div className="row g-3">
                            <div className="col-lg-6 col-12">
                                <Select
                                    label="Workers Comp Transfer Required?"
                                    fullWidth
                                    placeholder="Select option"
                                    options={TRANSFER_OPTIONS}
                                    name="workersCompTransferRequired"
                                    value={form.workersCompTransferRequired}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Current Workers Comp Carrier" fullWidth variant="flat"
                                    name="workersCompCarrier" value={form.workersCompCarrier} onChange={handleChange}
                                    placeholder="e.g. Liberty Mutual" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Policy Number" fullWidth variant="flat"
                                    name="workersCompPolicyNumber" value={form.workersCompPolicyNumber} onChange={handleChange}
                                    placeholder="Policy number" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Policy Effective Date" fullWidth variant="flat" type="date"
                                    name="workersCompEffectiveDate" value={form.workersCompEffectiveDate} onChange={handleChange} />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Policy Renewal / Expiration Date" fullWidth variant="flat" type="date"
                                    name="workersCompExpirationDate" value={form.workersCompExpirationDate} onChange={handleChange} />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Covered State(s)" fullWidth variant="flat"
                                    name="workersCompCoveredStates" value={form.workersCompCoveredStates} onChange={handleChange}
                                    placeholder="e.g. TN, TX, FL" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Class Code(s)" fullWidth variant="flat"
                                    name="workersCompClassCodes" value={form.workersCompClassCodes} onChange={handleChange}
                                    placeholder="e.g. 8810, 8742" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Experience Mod / Rate" fullWidth variant="flat"
                                    name="workersCompExperienceMod" value={form.workersCompExperienceMod} onChange={handleChange}
                                    placeholder="e.g. 0.92" />
                            </div>
                        </div>
                    </div>
                    <div className="border-bottom pb-4 mb-4">
                        <p className="info-title mb-3">Agent / Broker</p>
                        <div className="row g-3">
                            <div className="col-lg-6 col-12">
                                <Input label="Agent / Broker Name" fullWidth variant="flat"
                                    name="workersCompAgentName" value={form.workersCompAgentName} onChange={handleChange}
                                    placeholder="Agent name" />
                            </div>
                            <div className="col-lg-6 col-12">
                                <Input label="Agent Phone" fullWidth variant="flat"
                                    name="workersCompAgentPhone" value={form.workersCompAgentPhone} onChange={handleChange}
                                    placeholder="(615) 555-0100" />
                            </div>
                        </div>
                    </div>
                    <div className="pb-4 mb-4">
                        <p className="info-title mb-3">Notes</p>
                        <div className="row g-3">
                            <div className="col-12">
                                <textarea
                                    className="form-control"
                                    name="workersCompNotes"
                                    rows={4}
                                    value={form.workersCompNotes}
                                    onChange={handleChange}
                                    placeholder="Special transfer notes, exclusions, or open items"
                                />
                            </div>
                        </div>
                    </div>
                    {mutation.isError && (
                        <p className="text-danger mb-3">
                            {mutation.error?.response?.data?.message || "Failed to save. Please try again."}
                        </p>
                    )}
                    <div className="d-flex gap-2">
                        <button type="submit" className="button-orange py-2 px-4 rounded-pill" disabled={mutation.isPending}>
                            {mutation.isPending ? "Saving..." : "Save Changes"}
                        </button>
                        <button type="button" className="btn btn-outline-secondary rounded-pill py-2 px-4"
                            onClick={handleCancel} disabled={mutation.isPending}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    const transferLabel = TRANSFER_OPTIONS.find(o => o.value === company?.workersCompTransferRequired)?.label
                       || company?.workersCompTransferRequired;

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between">
                <h2 className="sub-title">Workers Compensation</h2>
                <button className="border-0 bg-transparent" onClick={() => setIsEditing(true)}>
                    <img className="cursor-pointer" src={editorange} alt="edit" />
                </button>
            </div>

            <div className="border-bottom pb-4 mb-4">
                <p className="info-title mb-3">Policy Information</p>
                <div className="general-information-section">
                    <Field label="Transfer Required?"        value={transferLabel} />
                    <Field label="Carrier"                   value={company?.workersCompCarrier} />
                    <Field label="Policy Number"             value={company?.workersCompPolicyNumber} />
                    <Field label="Effective Date"            value={company?.workersCompEffectiveDate} />
                    <Field label="Renewal / Expiration Date" value={company?.workersCompExpirationDate} />
                    <Field label="Covered State(s)"          value={company?.workersCompCoveredStates} />
                    <Field label="Class Code(s)"             value={company?.workersCompClassCodes} />
                    <Field label="Experience Mod / Rate"     value={company?.workersCompExperienceMod} />
                </div>
            </div>

            <div className="border-bottom pb-4 mb-4">
                <p className="info-title mb-3">Agent / Broker</p>
                <div className="general-information-section">
                    <Field label="Agent / Broker Name" value={company?.workersCompAgentName} />
                    <Field label="Agent Phone"          value={company?.workersCompAgentPhone} />
                </div>
            </div>

            {company?.workersCompNotes && (
                <div>
                    <p className="info-title mb-3">Notes</p>
                    <p className="m-0 sub-titles">{company.workersCompNotes}</p>
                </div>
            )}
        </div>
    );
};

export default WorkersCompensationSection;
